import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {AppState} from '../store';
import {select, Store} from '@ngrx/store';
import {selectFacebookConfig, selectTenantInfo, selectUser, selectUserPermissions} from '../store/selectors';
import {filter, tap} from 'rxjs/operators';
import {TenantService} from './tenant.service';
import {Tenant} from '../models/tenant.model';
import {User} from '../models/user.model';
import {environment} from '../../environments/environment';
import {NavigationEnd, Router} from '@angular/router';
import {TrackingEventType} from '../models/tracking-event-type.model';

// Facebook Tracking
declare const fbq;

declare const mixpanel;

// use google analytics to report conversions
declare const gtag;

/**
 *
 * This service is used to report events back to a platform like Facebook or Google.
 *
 * In the case of Facebook, this means events like Lead, InitiateCheckout, Purchase, etc.
 *
 * Some Facebook events are meant to be reported using the platform pixel.
 *
 * But others are meant to be reported using a pixel installed by the course creator, such as course purchase, etc.
 *
 * The correct pixel is set up at application startup time using the FacebookIntegrationComponent,
 *
 * so this component assumes that the correct pixel is already initialized and readyy to be used.
 *
 * Full list of supported Facebook events and their parameters:
 *
 * https://developers.facebook.com/docs/meta-pixel/reference
 *
 * Note: We have https://fb-conversions-api-gateway.onlinecoursehost.com installed
 *
 */

@Injectable({
  providedIn: 'root'
})
export class TrackingService {

  isAdmin: boolean;
  creatorPixelActive:boolean;
  tenant:Tenant;
  user:User

  constructor(
    private store: Store<AppState>,
    private router: Router) {

    this.store
      .pipe(
        select(selectUserPermissions),
        tap(permissions => {
          this.isAdmin = permissions?.isAdmin;
        })
      )
      .subscribe();

    this.store
      .pipe(
        select(selectFacebookConfig),
        tap(config => {
          this.creatorPixelActive = config?.enabled
        })
      )
      .subscribe();

    this.store
      .pipe(
        select(selectTenantInfo),
      )
      .subscribe(tenant => this.tenant = tenant as Tenant);

    this.store
      .pipe(
        select(selectUser)
      )
      .subscribe(user => this.user = user);

  }

  reportStudentPurchase(value:number) {
    if (this.isAnyFacebookPixelActive() && !this.isAdmin && this.creatorPixelActive) {
      console.log("FACEBOOK Reporting Purchase event");
      fbq(
        'track',
        'Purchase',
        {
          value,
          currency:this.tenant.currency
        });
    }
  }

  reportStudentInitiateCheckout() {
    if (this.isAnyFacebookPixelActive() && !this.isAdmin && this.creatorPixelActive) {
      console.log("FACEBOOK Reporting InitiateCheckout event");
      fbq(
        'track',
        'InitiateCheckout');
    }
  }
  reportStudentLead() {
    if (this.isAnyFacebookPixelActive() && !this.isAdmin && this.creatorPixelActive) {
      console.log("FACEBOOK Reporting Lead event");
      fbq('track',
        'Lead'
      );
    }
  }

  reportStudentSubscribe() {
    if (this.isAnyFacebookPixelActive() && !this.isAdmin && this.creatorPixelActive) {
      console.log("FACEBOOK Reporting Subscribe event");
      fbq('track',
        'Subscribe'
      );
    }
  }

  reportStudentViewContent() {
    if (this.isAnyFacebookPixelActive() && !this.isAdmin && this.creatorPixelActive) {
      console.log("FACEBOOK Reporting ViewContent event");
      fbq('track',
        'ViewContent'
      );
    }
  }

  /**
   *
   * This event reports the creation of a new creator account in OnlineCourseHost.com, after filling in the
   *
   * initial form and creating the subdomain.
   *
   */

  reportPlatformAccountCreation() {

    console.log(`Called reportPlatformAccountCreation()`);

    // we are no longer using the Pixel for this,
    // this is now reported on the server using the Facebook Conversions API
    return;

    setTimeout(() => {

      console.log(`FACEBOOK Reporting Platform Account Creation event. isAdmin = ${this.isAdmin}, isAnyFacebookPixelActive = ${this.isAnyFacebookPixelActive()}`);

      // check if it's an admin account, and that the platform pixel is active
      if (this.isAnyFacebookPixelActive()) {
        console.log("FACEBOOK Reporting Platform CompleteRegistration event");
        fbq(
          'track',
          'CompleteRegistration'
        );
      }

      this.reportMixPanelEvent('Account Created');


      }, 6000);

  }

  /**
   *
   * This event reports the activation of a platform subscription, meaning the Basic or Pro course creator plan.
   *
   */
  reportPlatformSubscription() {

    // we are no longer using the Pixel for this,
    // this is now reported on the server using the Facebook Conversions API
    return;

    setTimeout(() => {

      // check if it's an admin account, and that the platform pixel is active
      if (this.isAdmin && this.isAnyFacebookPixelActive()) {
        console.log("FACEBOOK Reporting Platform Subscribe Event");
        fbq('track',
          'Subscribe',
        );
      }

      this.reportMixPanelEvent('Subscription Created');

    }, 10000);

  }

  /**
   *
   * This event reports a platform one-time purchase, like for example the purchase of an instructor lifetime plan.
   *
   */
  reportPlatformPurchase(value:string) {
    // check if it's an admin account, and that the platform pixel is active
    if (this.isAdmin && this.isAnyFacebookPixelActive()) {
      console.log("FACEBOOK Reporting Platform Purchase Event");
      fbq('track', 'Purchase', {value, currency:this.tenant.currency});
    }

  }

  reportPlatformInitiateCheckout() {

    // we are no longer using the Pixel for this,
    // this is now reported on the server using the Facebook Conversions API
    return;

    if (this.isAdmin && this.isAnyFacebookPixelActive()) {
      console.log("FACEBOOK Reporting Platform InitiateCheckout event");
      fbq('track', 'InitiateCheckout');
    }
  }

  waitForEvent(facebookEvent:string) {
    // if the pixel is not installed, or this is not a student account, return immediately
    if (!this.isAnyFacebookPixelActive() || this.isAdmin || !this.creatorPixelActive) {
      return of(null);
    }
    // otherwise report the event to Facebook, and wait a bit for the request to complete
    else {
      return new Observable((observer)=> {

        fbq('track', facebookEvent);

        // wait for the event to reported to Facebook
        setTimeout(() => {
          observer.next(facebookEvent);
          observer.complete();
        }, 500);

      });
    }
  }

  private isAnyFacebookPixelActive() {
    return typeof fbq !='undefined';
  }

  reportCourseCreated() {
    this.reportMixPanelEvent('Course Created');
  }

  reportMixPanelEvent(event:TrackingEventType) {
    if (typeof mixpanel != 'undefined' && environment.production) {

      console.log(`Calling mixpanel.track('${event}') ...`);
      mixpanel.track(event);
    }
    else {
      console.log(`Mixpanel is not defined, could not report ${event}.`);
    }
  }

}
